import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { socket } from "../../socket";

import BasicCRUD from './../core/basic-crud';
import { socketUseEffect } from "../../socket";
import BadgeStatusView from "./sub-pages/elements/BadgeStatusView";
import { useAuth } from "../../AuthContext";
import ProgressBar from "../page-elements/progress-bar";
import FadeAlert from "../core/fade-alert";
import resolveUserRole from "../utils/resolveUserRole";
import RangeSlider from "../core/range-slider";

// ---------- i18n ----------
const dict = {
    en: {
        // titles
        title: "Web Sites",
        subTitle: "add / edit web sites",
        homeURL: "/dashboard",
        addTitle: "Add Web Site",
        editTitle: "Edit Web Site",

        // table columns
        colStatus: "Status",
        colURL: "URL",
        colTodayUptime: "Today Uptime",
        colResponse: "Response",
        colContactGroup: "Contact Group",

        // table cell text
        notAssigned: "— Not Assigned —",
        noName: "No Name",
        todayUptime: "Today Uptime:",
        lastUpdate: "Last Update:",
        invalidDate: "Invalid Date",

        // fields
        tabMain: "Main",
        name: "Name",
        server: "Server",
        protocol: "Protocol",
        url: "URL",
        port: "Port",
        basicAuth: "Basic Authorization",
        login: "Login",
        password: "Password",
        executeEvery: "Execute Every X Minutes",
        alertDelay: "Alert Delay",
        region: "Region",
        contactGroup: "Contact Group",

        // selects / options
        choose: " -- choose -- ",
        any: " -- Any -- ",
        yes: "Yes",
        no: "No",
        protocols: [
            { id: "http", name: "HTTP" },
            { id: "https", name: "HTTPS" },
            { id: "tcp", name: "TCP" },
            { id: "smtp", name: "SMTP" },
            { id: "ssh", name: "SSH" },
            { id: "ping", name: "PING" },
        ],
        minutesOptions: ["1","5","10","20","30","60"],
        numbers1to9: ["1","2","3","4","5","6","7","8","9"],

        // actions
        edit: "Edit",
        delete: "Delete",

        // quota/plan
        noResources: "You don't have any available resources. Please change your pricing plan to get more items.",
        planExpired: "Your pricing plan has expired. Please renew or upgrade your plan to continue accessing this feature.",

        // delete dialog
        delAlert: "Deleting a web site cannot be undone.",
        delText: "To confirm deletion, enter the name of the site in the next input field",
        delTitle: 'Delete Web service: "{name}"',

        // toasts
        toastEntity: "Web Site",
        createdOk: "created successfully",
        updatedOk: "updated successfully",
        deletedOk: "deleted successfully",
        memberViewOnly: "Members can only view web sites.",
    },

    ru: {
        title: "Веб-сайты",
        subTitle: "добавление / редактирование веб-сайтов",
        homeURL: "/dashboard",
        addTitle: "Добавить веб-сайт",
        editTitle: "Редактировать веб-сайт",

        colStatus: "Статус",
        colURL: "URL",
        colTodayUptime: "Аптайм сегодня",
        colResponse: "Ответ",
        colContactGroup: "Группа контактов",

        notAssigned: "— Не назначено —",
        noName: "Без названия",
        todayUptime: "Аптайм сегодня:",
        lastUpdate: "Последнее обновление:",
        invalidDate: "Неверная дата",

        tabMain: "Основное",
        name: "Название",
        server: "Сервер",
        protocol: "Протокол",
        url: "URL",
        port: "Порт",
        basicAuth: "Базовая авторизация",
        login: "Логин",
        password: "Пароль",
        executeEvery: "Выполнять каждые X минут",
        alertDelay: "Задержка оповещения",
        region: "Регион",
        contactGroup: "Группа контактов",

        choose: " -- выберите -- ",
        any: " -- Любой -- ",
        yes: "Да",
        no: "Нет",
        protocols: [
            { id: "http", name: "HTTP" },
            { id: "https", name: "HTTPS" },
            { id: "tcp", name: "TCP" },
            { id: "smtp", name: "SMTP" },
            { id: "ssh", name: "SSH" },
            { id: "ping", name: "PING" },
        ],
        minutesOptions: ["1","5","10","20","30","60"],
        numbers1to9: ["1","2","3","4","5","6","7","8","9"],

        edit: "Редактировать",
        delete: "Удалить",

        noResources: "У вас нет доступных ресурсов. Пожалуйста, измените тарифный план, чтобы получить больше элементов.",
        planExpired: "Срок действия вашего тарифного плана истёк. Обновите или продлите план, чтобы продолжить пользоваться функцией.",

        delAlert: "Удаление веб-сайта необратимо.",
        delText: "Чтобы подтвердить удаление, введите название сайта в следующее поле ввода",
        delTitle: 'Удалить веб-сервис: «{name}»',

        toastEntity: "Веб-сайт",
        createdOk: "успешно создан",
        updatedOk: "успешно обновлён",
        deletedOk: "успешно удалён",
        memberViewOnly: "У участников нет прав управлять веб-сайтами.",
    },

    ua: {
        title: "Веб-сайти",
        subTitle: "додавання / редагування веб-сайтів",
        homeURL: "/dashboard",
        addTitle: "Додати веб-сайт",
        editTitle: "Редагувати веб-сайт",

        colStatus: "Статус",
        colURL: "URL",
        colTodayUptime: "Аптайм сьогодні",
        colResponse: "Відповідь",
        colContactGroup: "Група контактів",

        notAssigned: "— Не призначено —",
        noName: "Без назви",
        todayUptime: "Аптайм сьогодні:",
        lastUpdate: "Останнє оновлення:",
        invalidDate: "Невірна дата",

        tabMain: "Основне",
        name: "Назва",
        server: "Сервер",
        protocol: "Протокол",
        url: "URL",
        port: "Порт",
        basicAuth: "Базова авторизація",
        login: "Логін",
        password: "Пароль",
        executeEvery: "Виконувати кожні X хвилин",
        alertDelay: "Затримка сповіщення",
        region: "Регіон",
        contactGroup: "Група контактів",

        choose: " -- оберіть -- ",
        any: " -- Будь-який -- ",
        yes: "Так",
        no: "Ні",
        protocols: [
            { id: "http", name: "HTTP" },
            { id: "https", name: "HTTPS" },
            { id: "tcp", name: "TCP" },
            { id: "smtp", name: "SMTP" },
            { id: "ssh", name: "SSH" },
            { id: "ping", name: "PING" },
        ],
        minutesOptions: ["1","5","10","20","30","60"],
        numbers1to9: ["1","2","3","4","5","6","7","8","9"],

        edit: "Редагувати",
        delete: "Видалити",

        noResources: "У вас немає доступних ресурсів. Будь ласка, змініть тарифний план, щоб отримати більше елементів.",
        planExpired: "Строк дії вашого тарифного плану минув. Поновіть або оновіть план, щоб продовжити користування функцією.",

        delAlert: "Видалення веб-сайту неможливо скасувати.",
        delText: "Щоб підтвердити видалення, введіть назву сайту у наступне поле вводу",
        delTitle: 'Видалити веб-сервіс: «{name}»',

        toastEntity: "Веб-сайт",
        createdOk: "успішно створено",
        updatedOk: "успішно оновлено",
        deletedOk: "успішно видалено",
        memberViewOnly: "У учасників немає прав керувати вебсайтами.",
    },
};

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

// ---------- component ----------
const WebSites = ({ lang: langProp, ...props }) => {
    const lang = (langProp || getInitialLang());
    const t = useMemo(() => dict[lang] || dict.en, [lang]);

    const object = "websites";
    const [response, setResponse] = useState({});
    const [fieldsData, setFieldsData] = useState({});

    const [totalData, setTotalData] = useState([]);
    const [contacts, setContacts] = useState([{ id: 0, name: t.choose }]);
    const [servers, setServers] = useState([{ id: 0, name: t.choose }]);
    const [regions, setRegions] = useState([{ id: 0, name: t.any }]);
    const navigate = useNavigate();
    const [afterDeletePath, setAfterDeletePath] = useState("/web-sites");

    const { user } = useAuth();
    const currentUserRole = useMemo(() => resolveUserRole(user), [user]);
    const canManageWebsites = currentUserRole !== "member";
    const listeners = [
        { name: "contacts", fn: "items-list", args: { user_id: user.user_id, site_id: props.site_id }, saveDataFN: setContacts },
        { name: "servers", fn: "items-list", args: { user_id: user.user_id, site_id: props.site_id }, saveDataFN: setServers },
        { name: "regions", fn: "items-list", args: { user_id: user.user_id, site_id: props.site_id }, saveDataFN: setRegions },
        { name: "dashboard", fn: "reports-total", args: {}, saveDataFN: setTotalData },
    ];
    useEffect(() => socketUseEffect(listeners, user), [response?.status, response?.created, response?.updated, response?.delete]);

    // Prefill form with details when editing single entity
    const hasInitialAlertDelay = fieldsData && fieldsData.alert_delay !== undefined;
    useEffect(() => {
        if (props.singleArgs?.edit && props.singleArgs.details && !hasInitialAlertDelay) {
            setFieldsData(props.singleArgs.details);
        }
    }, [props.singleArgs?.edit, props.singleArgs?.details?.alert_delay, hasInitialAlertDelay]);

    // After successful deletion in single view, prepare the next redirect (used on dialog close)
    useEffect(() => {
        if (!(props.singleArgs?.delete) || response?.delete !== 1) return;
        const eventName = `${object}:get-items-list-data`;
        const handler = (payload) => {
            socket.off(eventName, handler);

            // payload can be {data: [...]}, or an array, or ["event", [...]]
            const list =
                (payload && Array.isArray(payload.data) && payload.data)
                || (Array.isArray(payload) ? payload : [])
                || (Array.isArray(payload?.[1]) ? payload[1] : []);

            const currentId = props.singleArgs?.id || props.singleArgs?.details?.id;
            const candidates = (list || []).filter((row) => Number(row.id) !== 0 && Number(row.id) !== Number(currentId));
            const nextId = candidates?.[0]?.id;
            setAfterDeletePath(nextId ? `/web-site/${nextId}/dashboard` : "/web-sites");
        };
        socket.on(eventName, handler);
        socket.emit(`${object}:get-items-list`, {
            token: user.token,
            simple_search_text: "",
            order_by: "name",
            order_rule: "asc",
        });
        return () => socket.off(eventName, handler);
    }, [response?.delete, props.singleArgs?.delete, navigate, user.token]);

    const formatStatus = (data) => <BadgeStatusView status={data['status']} lang={lang} />;

    const formatFieldDate = (data, key) => {
        const date = new Date(data[key]);
        if (isNaN(date)) return t.invalidDate;
        const options = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };
        return date.toLocaleDateString('en-GB', options).replace(',', '');
    };

    const ResponseFormat = (data) => {
        const { response_status, response_time } = data;
        const formattedTime = Number.parseFloat(response_time).toFixed(2);
        return <>{response_status} ({formattedTime} s.)</>;
    };

    const formatShowURL = (data) => {
        const styles = {
            name: { fontWeight: 'bold', fontSize: 22, marginBottom: 4 },
            url: { fontSize: 12, color: '#6c757d' },
        };
        const webSiteUrl = `/web-site/${data.id}/dashboard`;
        const serverLabel = data['servers_name'] || t.noName;

        const content = (
            <>
                <Link to={webSiteUrl}><span style={styles.name}>{data.name || t.noName}</span></Link><br />
                <div className="text-muted small mt-1">Server: {serverLabel}</div>
            </>
        );

        // In table view we don't need to repeat the plain URL below the name
        return content;
    };

    const formatTodayUptime = (data) => {
        const fmt = (d) => {
            if (!d) return 'N/A';
            const date = new Date(d);
            return isNaN(date) ? 'N/A' : date.toLocaleDateString('en-US');
        };
        const lastUpdate = fmt(data['last_update']);
        let uptime = data['today_uptime'];
        uptime = (!uptime || uptime <= 0) ? 'N/A' : `${uptime}%`;

        const styles = {
            container: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' },
            label: { fontWeight: 'bold', fontSize: 14, color: '#333' },
            date: { fontSize: 12, color: '#6c757d' },
            row: { marginBottom: 4 },
        };

        return (
            <div style={styles.container}>
                <div style={styles.row}>
                    <span style={styles.label}>{t.todayUptime}</span>{' '}
                    <span style={styles.date}>{uptime}</span>
                </div>
                <div style={styles.row}>
                    <span style={styles.label}>{t.lastUpdate}</span>{' '}
                    <span style={styles.date}>{lastUpdate}</span>
                </div>
            </div>
        );
    };

    const columns = [
        { label: t.colStatus, key: "status", format: formatStatus },
        { label: t.colURL, key: "url", format: formatShowURL },
        { label: t.colTodayUptime, key: "today_uptime", format: formatTodayUptime },
        { label: t.colResponse, key: "response", format: ResponseFormat },
        { label: t.colContactGroup, key: "contacts_group_name" },
    ];

    const showField = (data, field) => {
        const key = field.key;
        const updated = { ...field };
        if ((key === "basic_auth_username" || key === "basic_auth_password") && String(data['basic_auth']) === "0") {
            updated.readonly = true;
        }
        if (key === "port" && data['protocol'] === "ping") {
            updated.readonly = true;
        }
        return updated;
    };

    const fields = [
        {
            type: "tab",
            name: t.tabMain,
            showField,
            fields: [
                [
                    { label: t.name, key: "name", default_value: "", showLabel: "top" },
                    { label: t.server, key: "server_id", default_value: "", type: "selectbox", data: servers, showLabel: "top" },
                ],
                [
                    { label: t.protocol, key: "protocol", default_value: "https", type: "selectbox", data: t.protocols, showLabel: "top" },
                    { label: t.url, key: "url", default_value: "", showLabel: "top" },
                    { label: t.port, key: "port", default_value: "", showLabel: "top" },
                ],
                [
                    { label: t.basicAuth, key: "basic_auth", type: "selectbox", data: [{ id: '1', name: t.yes }, { id: '0', name: t.no }], default_value: "0", showLabel: "top" },
                    { label: t.login, key: "basic_auth_username", default_value: "", showLabel: "top" },
                    { label: t.password, key: "basic_auth_password", default_value: "", showLabel: "top" },
                ],
                [
                    { label: t.executeEvery, key: "load_interval", default_value: "", type: "selectbox", data: t.minutesOptions.map(m => ({ id: m, name: m })), showLabel: "top" },
                    {
                        label: t.alertDelay,
                        key: "alert_delay",
                        default_value: "2",
                        type: "custom",
                        showLabel: "top",
                        fn: ({ fieldsChange, fieldsData, field, data }) => {
                            const dataSafe = fieldsData || {};

                            const val = Number(
                                data[field.key] ??
                                dataSafe[field.key] ??
                                field.default_value ??
                                2,
                            ) || 2;

                            return (
                                <RangeSlider
                                    value={val}
                                    label={t.alertDelay}
                                    min={0}
                                    max={9}
                                    step={1}
                                    leftLabel="0"
                                    rightLabel="9"
                                    onChange={(next) => {
                                        const fakeEvent = { target: { value: next } };
                                        fieldsChange(fakeEvent, field.key);
                                    }}
                                />
                            );
                        },
                    },
                ],
                [
                    { label: t.region, key: "region", default_value: "0", type: "selectbox", data: regions, showLabel: "top" },
                    { label: t.contactGroup, key: "contact_group_id", default_value: "0", type: "selectbox", data: contacts, showLabel: "top" },
                ],
            ],
        },
    ];

    const actions = canManageWebsites ? [
        { name: "edit", label: t.edit, controller: "crud", icon: "ti ti-edit" },
        { name: "delete", label: t.delete, controller: "crud", icon: "ti ti-trash" },
    ] : [];

    const renderWebSiteCard = (item, helpers) => {
        const responseBlock = ResponseFormat(item);
        const uptimeBlock = formatTodayUptime(item);
        const urlBlock = formatShowURL(item);
        const contactGroup = item.contacts_group_name || t.notAssigned;
        const actionsList = helpers.actions || [];
        const websiteUrl = `/web-site/${item.id}/dashboard`;

        return (
            <Link to={websiteUrl} className="text-decoration-none text-dark">
                <div className="card h-100 shadow-sm border">
                    <div className="card-body d-flex flex-column">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            {formatStatus(item)}
                            <span className="btn btn-sm btn-outline-primary">
                                <i className="ti ti-arrow-up-right"></i>
                            </span>
                        </div>

                        <div className="mb-3">{urlBlock}</div>
                        <div className="mb-3">{uptimeBlock}</div>
                        <div className="mb-3">{responseBlock}</div>

                        <div className="text-muted small mb-4">
                            {t.colContactGroup}: <span className="fw-semibold text-dark">{contactGroup}</span>
                        </div>

                        {actionsList.length > 0 && (
                            <div className="mt-auto d-flex gap-2 justify-content-end flex-wrap">
                                {actionsList.map((action) => (
                                    <button
                                        key={action.name}
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        title={action.label}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            helpers.tableAction?.(action.name, item);
                                        }}
                                    >
                                        <i className={action.icon || "ti ti-dots"}></i>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </Link>
        );
    };

    const expired = user?.expired ?? 1;
    const disabled = !canManageWebsites
        ? { add: { disable: true, title: t.memberViewOnly } }
        : (expired
            ? { add: { disable: true, title: t.planExpired } }
            : {
                add: {
                    disable: (totalData.webservices - totalData.used_webservices) < 1,
                    title: t.noResources,
                },
            });

    return (
        <>
            <BasicCRUD
                singleArgs={props?.singleArgs ?? false}
                title={t.title}
                subTitle={t.subTitle}
                homeURL={t.homeURL}
                addTitle={t.addTitle}
                disable={disabled}
                editTitle={t.editTitle}
                object={object}
                columns={columns}
                fields={fields}
                actions={actions}
                fieldsData={fieldsData}
                setFieldsData={setFieldsData}
                response={response}
                setResponse={setResponse}
                ownHandler={true}
                singleFormWrapperClass="card border-0 shadow-sm rounded-4 bg-light-subtle p-4"
                singleFormInnerClass="row g-4"
                singleFormButtonsClass="d-flex justify-content-end"
                singleFormButtonStyle={{ marginTop: 0, marginBottom: 0, minWidth: "160px" }}
                afterDeleteRedirect={afterDeletePath}
                deleteConfirmationDialog={{
                    alert: t.delAlert,
                    text: t.delText,
                    title: t.delTitle,
                    column: 'name',
                }}
                viewModes={["table", "blocks"]}
                renderItem={renderWebSiteCard}
                cardLayout={{ columnClass: "col-12 col-lg-6 col-xxl-4" }}
            >
                {!props.singleArgs && (
                    <ProgressBar
                        lang={lang}
                        used={totalData.used_webservices}
                        allowed={totalData.webservices}
                    />
                )}

                {props.singleArgs?.edit && (
                    <div className="alert bg-primary-subtle border-0 text-primary-emphasis rounded-4 mb-3">
                        <div className="fw-semibold mb-1">{t.editTitle}</div>
                        <div className="small mb-0">
                            Tune protocol, checks interval, auth and alerts for this site. Save when finished.
                        </div>
                    </div>
                )}

                {response.status === 1 ? (
                    <FadeAlert response={response} resetResponse={() => setResponse([])}>
                        {response.created === 1 && (<><strong>{t.toastEntity}</strong> {t.createdOk}</>)}
                        {response.updated === 1 && (<><strong>{t.toastEntity}</strong> {t.updatedOk}</>)}
                        {response.delete === 1 && (<><strong>{t.toastEntity}</strong> {t.deletedOk}</>)}
                    </FadeAlert>
                ) : null}
            </BasicCRUD>
        </>
    );
};

export default WebSites;
